import React, { useState } from 'react';
import { motion } from 'framer-motion';
import StripePaymentForm from './StripePaymentForm';
import { useAuth } from '../AuthContext';
import { COLOR, FONT, glassHeavy } from '../theme';

function GuestCheckout({ total, roomNumber, hotelId, restaurantId, items, notes, onConfirm, onCancel }) {
  const { user } = useAuth();

  if (!user) {
    return (
      <GuestInfoForm
        total={total}
        roomNumber={roomNumber}
        hotelId={hotelId}
        restaurantId={restaurantId}
        items={items}
        notes={notes}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    );
  }

  const guestInfo = {
    name: user.name || 'Guest',
    phone: user.phone || '',
    email: user.email || '',
    hotelId,
  };

  return (
    <StripePaymentForm
      total={total}
      roomNumber={roomNumber}
      restaurantId={restaurantId}
      guestInfo={guestInfo}
      items={items}
      notes={notes}
      onSuccess={(paymentIntentId) => onConfirm({ ...guestInfo, paymentIntentId })}
      onCancel={onCancel}
    />
  );
}

function GuestInfoForm({ total, roomNumber, hotelId, restaurantId, items, notes, onConfirm, onCancel }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [nameErr, setNameErr] = useState('');
  const [phoneErr, setPhoneErr] = useState('');
  const [step, setStep] = useState('form');
  const [guestInfo, setGuestInfo] = useState(null);

  const handleContinue = () => {
    let ok = true;
    if (!name.trim()) { setNameErr('Please enter your name'); ok = false; }
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 8) { setPhoneErr('Please enter a valid phone number'); ok = false; }
    if (!ok) return;
    const info = { name: name.trim(), phone: phone.trim(), email: '', hotelId };
    setGuestInfo(info);
    setStep('payment');
  };

  if (step === 'payment' && guestInfo) {
    return (
      <StripePaymentForm
        total={total}
        roomNumber={roomNumber}
        restaurantId={restaurantId}
        guestInfo={guestInfo}
        items={items}
        notes={notes}
        onSuccess={(paymentIntentId) => onConfirm({ ...guestInfo, paymentIntentId })}
        onCancel={onCancel}
      />
    );
  }

  return (
    <div style={s.overlay} onClick={onCancel}>
      <motion.div
        initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 320 }}
        style={s.sheet} onClick={(e) => e.stopPropagation()}
      >
        <div style={s.handle} />
        <h2 style={s.title}>Your details</h2>
        <p style={s.sub}>So we can confirm your order.</p>

        <div style={s.field}>
          <label style={s.label}>NAME</label>
          <input
            style={{ ...s.input, borderColor: nameErr ? '#ff6b6b' : 'rgba(255,255,255,0.08)' }}
            placeholder="Your name"
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); setNameErr(''); }}
            autoFocus
          />
          {nameErr && <p style={s.error}>{nameErr}</p>}
        </div>

        <div style={s.field}>
          <label style={s.label}>PHONE NUMBER</label>
          <input
            style={{ ...s.input, borderColor: phoneErr ? '#ff6b6b' : 'rgba(255,255,255,0.08)' }}
            placeholder="+61 400 000 000"
            type="tel"
            value={phone}
            onChange={(e) => { setPhone(e.target.value); setPhoneErr(''); }}
          />
          {phoneErr && <p style={s.error}>{phoneErr}</p>}
        </div>

        <div style={s.roomRow}>
          <span style={s.roomLabel}>ROOM</span>
          <span style={s.roomValue}>{roomNumber}</span>
        </div>

        <motion.button whileTap={{ scale: 0.98 }} style={s.continueBtn} onClick={handleContinue}>
          Continue to payment →
        </motion.button>
        <button style={s.cancelBtn} onClick={onCancel}>Cancel</button>
      </motion.div>
    </div>
  );
}

// GoogleIcon lived here unused — the live one is in LoginSheet.js, which is the
// only place a Google button is actually rendered. Removed rather than kept as a
// second copy that would drift.

const s = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', zIndex: 300, display: 'flex', alignItems: 'flex-end' },
  sheet: { ...glassHeavy({ borderRadius: '28px 28px 0 0' }), padding: '14px 24px 40px', width: '100%', maxWidth: '430px', margin: '0 auto', fontFamily: FONT },
  handle: { width: '38px', height: '4px', background: 'rgba(255,255,255,0.2)', borderRadius: '2px', margin: '0 auto 24px' },
  title: { fontSize: '24px', fontWeight: '700', color: COLOR.text, margin: '0 0 6px' },
  sub: { fontSize: '14px', color: COLOR.textMuted, margin: '0 0 24px', lineHeight: 1.5 },
  field: { display: 'flex', flexDirection: 'column', gap: '7px', marginBottom: '16px' },
  label: { fontSize: '10px', letterSpacing: '2px', color: COLOR.textFaint, fontWeight: '600' },
  input: { background: 'rgba(255,255,255,0.04)', border: '1px solid', borderRadius: '13px', padding: '14px 16px', color: COLOR.text, fontSize: '16px', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' },
  error: { fontSize: '13px', color: '#ff6b6b', margin: '4px 0 0' },
  roomRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(255,255,255,0.04)', border: `1px solid ${COLOR.glassBorder}`, borderRadius: '13px', padding: '13px 16px', marginBottom: '16px' },
  roomLabel: { fontSize: '10px', letterSpacing: '2px', color: COLOR.textFaint, fontWeight: '600' },
  roomValue: { fontSize: '16px', fontWeight: '700', color: COLOR.text },
  continueBtn: { width: '100%', background: `linear-gradient(135deg, ${COLOR.accent} 0%, ${COLOR.accentDeep} 100%)`, color: '#fff', border: 'none', borderRadius: '15px', padding: '16px', fontSize: '16px', fontWeight: '700', fontFamily: 'inherit', cursor: 'pointer', marginTop: '8px', marginBottom: '10px' },
  cancelBtn: { width: '100%', background: 'transparent', color: COLOR.textFaint, border: `1px solid ${COLOR.glassBorder}`, borderRadius: '14px', padding: '14px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
};

export default GuestCheckout;
